import {
  createRouter,
  createWebHashHistory,
  type RouteRecordRaw,
  type RouteRecordRedirectOption,
} from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import DefaultLayout from '@/layouts/DefaultLayout.vue'
import { cancelActiveRequests, onUnauthorized } from '@/libraries/axios'

const accountPage: RouteRecordRedirectOption = (to) =>
  `/tenant/${to.params.tenant}/account/${String(to.params.api).toLowerCase()}`

const routes: Array<RouteRecordRaw> = [
  {
    path: '/',
    component: DefaultLayout,
    redirect: '/dashboard',
    meta: { requiresAuth: true },
    children: [
      {
        path: '/dashboard',
        component: () => import('@/views/Dashboard.vue'),
      },
      {
        path: '/tenant/:tenant',
        children: [
          {
            path: 'device',
            component: () => import('@/views/device/index.vue'),
          },
          {
            path: 'triage',
            component: () => import('@/views/triage/index.vue'),
          },
          /* Saved `logs` links open the triage. */
          {
            path: 'logs',
            redirect: (to) => `/tenant/${to.params.tenant}/triage`,
          },
          {
            path: 'account/:api(ssh|snmp|http)',
            sensitive: true,
            component: () => import('@/views/service/index.vue'),
          },
          /* Saved links: the protocol in another case, or without `account/`. */
          {
            path: 'account/:api(ssh|snmp|http)',
            redirect: accountPage,
          },
          {
            path: ':api(ssh|snmp|http)',
            redirect: accountPage,
          },
        ],
      },
    ],
  },
  {
    path: '/login',
    name: 'login',
    component: () => import('@/views/pages/Login.vue'),
  },
  {
    path: '/:notFound(.*)',
    component: () => import('@/views/pages/404.vue'),
  },
]

const router = createRouter({
  history: createWebHashHistory(),
  routes,
  scrollBehavior: () => ({ top: 0 }),
})

router.beforeEach(async (to, from) => {
  /* Only on a path change: a query change does not remount the view. */
  if (to.path !== from.path) cancelActiveRequests()
  if (!to.meta.requiresAuth) return
  const authStore = useAuthStore()
  if (authStore.user === null) await authStore.checkAuth()
  if (!authStore.isAuthenticated) return { name: 'login' }
})

onUnauthorized(() => {
  if (router.currentRoute.value.name !== 'login') router.push({ name: 'login' })
})

export default router
